import React, { useMemo } from "react"

import { format } from "date-fns"
import { ChevronRight, Clock, DollarSign, MapPin, Truck, User } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  useCollectionsConfig,
  useRoutesPlanned,
  useVehicleTypes,
} from "@/hooks/useCollections"
import { StatusDot, StatusPill } from "@/components/ui/status-pill"
import { getRouteConcernScore, getRouteStatusDisplay } from "@/lib/statusDisplay"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { Route } from "@/types/collections"
import { calculateRouteCost, formatCost, priceRoute } from "@/lib/routeCostCalculation"

import RouteFilters from "./RouteFilters"

interface RouteListPanelProps {
  routes: Route[]
  selectedRoute: Route | null
  onRouteSelect: (route: Route) => void
  onRouteClick: (route: Route) => void
  // Controlled by the parent so the status filter also drives the map and stats bar; falls back to
  // internal state.
  statusFilter?: string[]
  onStatusFilterChange?: (value: string[] | ((prev: string[]) => string[])) => void
  // Controlled so the stat band can switch to "concern" when it turns alarms on.
  sortBy?: string
  onSortByChange?: (value: string) => void
}

const RouteListPanel: React.FC<RouteListPanelProps> = ({
  routes,
  selectedRoute,
  onRouteSelect,
  onRouteClick,
  statusFilter: statusFilterProp,
  onStatusFilterChange,
  sortBy: sortByProp,
  onSortByChange,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("")
  const [internalStatusFilter, setInternalStatusFilter] = React.useState<string[]>([])
  const statusFilter = statusFilterProp ?? internalStatusFilter
  const setStatusFilter = onStatusFilterChange ?? setInternalStatusFilter
  const [depotFilter, setDepotFilter] = React.useState<string[]>([])
  const [internalSortBy, setInternalSortBy] = React.useState<string>("none")
  const sortBy = sortByProp ?? internalSortBy
  const setSortBy = onSortByChange ?? setInternalSortBy

  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()
  const { data: routesPlannedData } = useRoutesPlanned()

  const plannedRouteVehicleTypes = useMemo(() => {
    const map = new Map<string, string>()
    if (routesPlannedData?.data) {
      routesPlannedData.data.forEach((rp) => {
        if (rp.vehicle_type) {
          map.set(rp.id, rp.vehicle_type)
        }
      })
    }
    return map
  }, [routesPlannedData])

  const routeCosts = useMemo(() => {
    const costs = new Map<string, ReturnType<typeof calculateRouteCost>>()
    if (!vehicleTypesData?.data || !configData?.data) return costs

    routes.forEach((route) => {
      const cost = priceRoute({
        vehicleTypes: vehicleTypesData.data,
        vehicleTypeRef:
          route.vehicleType ||
          (route.plannedRouteId ? plannedRouteVehicleTypes.get(route.plannedRouteId) : undefined),
        config: configData.data,
        distanceMiles: route.distanceMiles,
        durationMinutes: route.durationMinutes,
        day: { stops: route.stops, crewSize: route.crewSize, routeKind: route.routeKind },
      })
      if (cost) costs.set(route.id, cost)
    })

    return costs
  }, [routes, vehicleTypesData, configData, plannedRouteVehicleTypes])

  const uniqueDepots = React.useMemo(() => {
    const depots = new Set(routes.map((route) => route.depot))
    return Array.from(depots).sort()
  }, [routes])

  const toggleStatus = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    )
  }

  const toggleDepot = (depot: string) => {
    setDepotFilter((prev) =>
      prev.includes(depot) ? prev.filter((d) => d !== depot) : [...prev, depot],
    )
  }

  const filteredRoutes = routes.filter((route) => {
    const matchesSearch =
      route.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.vehicleReg.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(route.status)

    const matchesDepot = depotFilter.length === 0 || depotFilter.includes(route.depot)

    return matchesSearch && matchesStatus && matchesDepot
  })

  const sortedRoutes = React.useMemo(() => {
    const sorted = [...filteredRoutes]

    switch (sortBy) {
      case "concern": {
        // Scored once per route off a single clock reading, so the order can't shift mid-sort.
        const now = new Date()
        const scores = new Map(sorted.map((r) => [r.id, getRouteConcernScore(r, now)]))
        return sorted.sort((a, b) => (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0))
      }
      case "most_behind":
        return sorted.sort((a, b) => (b.delayMinutes || 0) - (a.delayMinutes || 0))
      case "most_ahead":
        return sorted.sort((a, b) => (a.delayMinutes || 0) - (b.delayMinutes || 0))
      case "route_code":
        return sorted.sort((a, b) => a.code.localeCompare(b.code))
      case "driver_name":
        return sorted.sort((a, b) => a.driverName.localeCompare(b.driverName))
      case "start_time":
        return sorted.sort((a, b) => a.plannedStartTime.getTime() - b.plannedStartTime.getTime())
      case "progress":
        return sorted.sort((a, b) => {
          const aProgress = a.totalStops > 0 ? a.completedStops / a.totalStops : 0
          const bProgress = b.totalStops > 0 ? b.completedStops / b.totalStops : 0
          return bProgress - aProgress
        })
      default:
        return sorted
    }
  }, [filteredRoutes, sortBy])

  return (
    <div className="w-80 bg-white border-r border-palette-jungleGreen200 flex flex-col min-h-0">
      <RouteFilters
        className="flex-shrink-0 border-b border-palette-jungleGreen200 p-3"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusToggle={toggleStatus}
        depots={uniqueDepots}
        depotFilter={depotFilter}
        onDepotToggle={toggleDepot}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      {/* Radix's viewport wrapper is display:table and grows to its content; this stops a long
          route code widening the panel. */}
      <ScrollArea className="min-h-0 flex-1 [&>div>div]:!block [&>div>div]:!w-full">
        <div className="p-2 space-y-1 overflow-hidden">
          {sortedRoutes.map((route) => {
            const status = getRouteStatusDisplay(route)
            // Flagged here because nothing else on this board says so.
            const hasDirectInjection = route.stops?.some((s) => s.slotType === "direct_injection")
            return (
            <div
              key={route.id}
              className={cn(
                "p-3 rounded-lg cursor-pointer transition-all duration-150",
                selectedRoute?.id === route.id
                  ? "bg-amber-50 border border-amber-300"
                  : "bg-white border border-palette-jungleGreen200 hover:bg-palette-jungleGreen000 hover:border-palette-jungleGreen300",
              )}
              onClick={() => {
                onRouteSelect(route)
                onRouteClick(route)
              }}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <StatusDot tone={status.tone} className="mt-1 h-2 w-2 flex-shrink-0" />
                  <span className="min-w-0 break-words text-sm font-semibold text-palette-jungleGreen900">
                    {route.code}
                  </span>
                </div>
                <StatusPill
                  tone={status.tone}
                  label={status.detailLabel}
                  className="flex-shrink-0"
                />
              </div>

              <div className="space-y-1.5 text-xs">
                {/* An unassigned route used to render as a bare icon with nothing beside it,
                    which reads as a rendering fault rather than as missing data. Say so. */}
                <div className="flex items-center gap-2 text-palette-jungleGreen600">
                  <Truck size={12} className="flex-shrink-0 text-palette-jungleGreen400" />
                  <span
                    className={cn(
                      "min-w-0 truncate",
                      !route.vehicleReg?.trim() && "italic text-palette-jungleGreen400",
                    )}
                  >
                    {route.vehicleReg?.trim() || "No vehicle"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-palette-jungleGreen600">
                  <User size={12} className="flex-shrink-0 text-palette-jungleGreen400" />
                  <span
                    className={cn(
                      "min-w-0 truncate",
                      !route.driverName?.trim() && "italic text-palette-jungleGreen400",
                    )}
                  >
                    {route.driverName?.trim() || "No driver"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-palette-jungleGreen600">
                  <Clock size={12} className="flex-shrink-0 text-palette-jungleGreen400" />
                  <span className="min-w-0 truncate">
                    {format(route.plannedStartTime, "HH:mm")} -{" "}
                    {format(route.plannedEndTime, "HH:mm")}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-palette-jungleGreen600">
                    <MapPin size={12} className="text-palette-jungleGreen400" />
                    <span>
                      {route.completedStops}/{route.totalStops} stops
                    </span>
                  </div>
                  <ChevronRight size={14} className="text-palette-jungleGreen400" />
                </div>
                {(routeCosts.get(route.id) || hasDirectInjection) && (
                  <div className="flex items-center justify-between gap-2 mt-1">
                    {routeCosts.get(route.id) ? (
                      <div className="flex items-center gap-2 text-palette-jungleGreen600">
                        <DollarSign size={12} className="text-palette-jungleGreen400" />
                        <span className="font-medium text-palette-jungleGreen900">
                          {formatCost(routeCosts.get(route.id)!.total_cost)}
                        </span>
                      </div>
                    ) : (
                      <span />
                    )}
                    {hasDirectInjection && (
                      <Badge
                        variant="outline"
                        className="flex-shrink-0 border-palette-jungleGreen200 px-1.5 py-0 text-[10px] text-palette-jungleGreen500"
                      >
                        Not sent to Track-POD
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Only partial progress is worth a bar; 0% and 100% repeat the text above. */}
              {status.key !== "completed" &&
                status.key !== "cancelled" &&
                route.totalStops > 0 &&
                route.completedStops > 0 &&
                route.completedStops < route.totalStops && (
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-palette-jungleGreen100">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        toneClasses(status.tone).dot,
                      )}
                      style={{ width: `${(route.completedStops / route.totalStops) * 100}%` }}
                    />
                  </div>
                )}
            </div>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

export default RouteListPanel
